import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import {
  CreditCardForm,
  CreditCardFormAPI,
} from '@wix/payments-cc-form/dist/lib/CreditCardForm';
import { ValidateResult } from '@wix/payments-cc-form/dist/src/types/ValidateResult';
import React from 'react';
import {
  AppInstance,
  RevalidateAppInstance,
} from '@wix/cashier-common/dist/src/types/AppInstance';
import { DeviceType } from '../../../../types/DeviceType';
import { Theme } from '../../../../types/Theme';
import { getConsentPolicyHeader } from '../../../../utils/consentPolicy';
import { CommonMethodProps, SavedCardProp } from '../types';
import { CreditCardView } from './view';
import { ICashierSDKPublicApi } from '../../../../sdk/cashier-sdk';
import { PaymentFailResult } from '../../../../sdk/types/PaymentCompleteResult';

export interface CreditCardControllerProps extends CommonMethodProps {
  appId: string;
  appInstanceId: string;
  appInstance?: AppInstance;
  locale: string;
  msid: string;
  installments?: {
    minInstallments: number;
    maxInstallments: number;
  };
  paymentsSDK: ICashierSDKPublicApi;
  providerId: PaymentProvider;
  deviceType: DeviceType;
  tokenizeMode: boolean;
  isSaveCCEnabled?: boolean;
  savedCard?: SavedCardProp;
  countryCode?: string;
  orderId: string;
  visitorId: string;
  siteOwnerId: string;
  ccFormUrlOverride?: string;
  // Square only
  squareAppId?: string;
  // TwoCheckout only
  sellerId?: string;
  publishableKey?: string;

  setSaveCardCheckbox(isChecked: boolean): void;
  clearSaveCardCheckbox(): void;
  resetCardInstallments(): void;
  revalidateAppInstance?: RevalidateAppInstance;
  onLoad(): void;
  onCardFormApiInit(api: CreditCardFormAPI): void;
  onChange(validityState: ValidateResult): void;
}

interface CreditCardControllerState {
  isLoading: boolean;
  isError: boolean;
  isCVVForceRequired: boolean;
}

export class CreditCardController extends React.Component<
  CreditCardControllerProps,
  CreditCardControllerState
> {
  private isComponentMounted: boolean = false;

  state = {
    isError: false,
    isCVVForceRequired: false,
    isLoading: true,
  };

  isSaveCardAvailable(props: CreditCardControllerProps = this.props): boolean {
    const { savedCard, isSaveCCEnabled } = props;
    return isSaveCCEnabled && !savedCard;
  }

  get ccFormProps(): React.ComponentProps<typeof CreditCardForm> {
    return {
      baseURLOverride: this.props.ccFormUrlOverride,
      // baseURLOverride: 'https://localhost:3005/',
      appId: this.props.appId,
      appInstanceId: this.props.appInstanceId,
      appInstance: this.props.appInstance,
      providerId: this.props.providerId,
      country: this.props.countryCode,
      locale: this.props.locale,
      msid: this.props.msid,
      deviceType: this.props.deviceType,
      orderId: this.props.orderId,
      ...(this.props.visitorId && {
        visitorId: this.props.visitorId,
      }),
      ...(this.props.siteOwnerId && {
        siteOwnerId: this.props.siteOwnerId,
      }),
      ...(this.props.theme === Theme.modal && {
        theme: 'minimal',
      }),

      ...(this.props.savedCard && {
        savedCard: {
          id: this.props.savedCard.id,
          network: this.props.savedCard.network,
          withCVV:
            this.props.savedCard.isCVVRequired || this.state.isCVVForceRequired,
          withExpireDate: this.props.savedCard.isExpired,
          expiration: this.props.savedCard.expiration,
        },
      }),

      ...(this.props.providerId === PaymentProvider.Square &&
        this.props.squareAppId && {
          squareAppId: this.props.squareAppId,
        }),

      ...(this.props.providerId === PaymentProvider.TwoCheckout &&
        this.props.sellerId &&
        this.props.publishableKey && {
          sellerId: this.props.sellerId,
          publishableKey: this.props.publishableKey,
        }),

      consentPolicyHeader: getConsentPolicyHeader(),

      onLoad: () => {
        this.setState({ isLoading: false }, this.handleLoad);
      },

      onCrash: () => {
        this.setState({ isLoading: false, isError: true }, this.handleLoad);
      },

      onApiInit: this.props.onCardFormApiInit,
      onChange: this.props.onChange,

      revalidateAppInstance: () => this.props.revalidateAppInstance?.(),
    };
  }

  safeSetState = (state: Partial<CreditCardControllerState>, callback?) => {
    if (this.isComponentMounted) {
      this.setState(state as CreditCardControllerState, callback);
    }
  };

  handleFailedPayment = (failResult: PaymentFailResult) => {
    if (
      failResult.clientStatus === 'Failed' &&
      failResult.paymentMethod === PaymentMethod.CreditCard &&
      !!this.props.savedCard &&
      !this.props.savedCard?.isCVVRequired
    ) {
      this.safeSetState({ isCVVForceRequired: true });
    }
  };

  handleLoad = () => {
    this.props.onLoad?.();
  };

  componentDidMount() {
    this.isComponentMounted = true;

    if (this.isSaveCardAvailable()) {
      this.props.setSaveCardCheckbox(true);
    }

    this.props.paymentsSDK.on('payment:failed', this.handleFailedPayment);
  }

  componentDidUpdate(
    prevProps: CreditCardControllerProps,
    prevState: CreditCardControllerState
  ) {
    if (this.isSaveCardAvailable(prevProps) !== this.isSaveCardAvailable()) {
      this.props.setSaveCardCheckbox(this.isSaveCardAvailable());
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    this.props.clearSaveCardCheckbox();
    this.props.resetCardInstallments();
    this.props.paymentsSDK.off('payment:failed', this.handleFailedPayment);
  }

  render() {
    return (
      <CreditCardView
        isLoading={this.state.isLoading}
        isError={this.state.isError}
        isSaveCardAvailable={this.isSaveCardAvailable()}
        deviceType={this.props.deviceType}
        theme={this.props.theme}
        isSinglePaymentOption={this.props.isSinglePaymentOption}
        tokenizeMode={this.props.tokenizeMode}
        installments={this.props.installments}
        isSavedCard={!!this.props.savedCard}
        ccFormProps={this.ccFormProps}
        isCardFormCollapsed={
          !!this.props.savedCard?.id &&
          !this.props.savedCard?.isExpired &&
          !this.props.savedCard?.isCVVRequired &&
          !this.state.isCVVForceRequired
        }
      />
    );
  }
}
